import React, { useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useOfimaPedidoDetalle } from '../../../hooks/useOfimaPedidos'
import { facturaTiposVisibles } from '../../../utils/ofimaPedidos'
import type { OfimaDocumento, OfimaRemisionDetalle } from '../../../types/ofimaPedidos'
import OfimaDocumentoCard from './OfimaDocumentoCard'
import OfimaTipoBadge from './OfimaTipoBadge'

interface PedidoOfimaDetailModalProps {
  orden: string | null
  isOpen: boolean
  tipodcto?: string
  onClose: () => void
}

function documentKey(documento: OfimaDocumento, index: number): string {
  const tipo = documento.encabezado?.tipodcto ?? 'doc'
  const numero = documento.encabezado?.nrodcto ?? index
  return `${tipo}-${numero}-${index}`
}

function ResumenItem({
  label,
  value,
  hint
}: {
  label: string
  value: string | number
  hint?: string
}) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900">{value}</p>
      {hint ? <p className="mt-0.5 text-xs text-blue-700">{hint}</p> : null}
    </div>
  )
}

const PedidoOfimaDetailModal: React.FC<PedidoOfimaDetailModalProps> = ({
  orden,
  isOpen,
  tipodcto,
  onClose
}) => {
  const { detalle, isLoading, error } = useOfimaPedidoDetalle(orden, isOpen, tipodcto)

  useEffect(() => {
    if (!isOpen) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen || !orden) return null

  const resumen = detalle?.resumen
  const remisiones = detalle?.remisiones ?? []
  const adicionales = detalle?.documentosAdicionales ?? []
  const tipos = resumen ? facturaTiposVisibles(resumen) : []

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gray-900/40 p-4">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Cerrar detalle del pedido"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pedido-ofima-title"
        className="relative z-10 flex max-h-[min(900px,calc(100vh-2rem))] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Pedido Ofima</p>
            <h3 id="pedido-ofima-title" className="font-mono text-lg font-semibold text-gray-900">
              {orden}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="space-y-3" role="status" aria-label="Cargando pedido">
              <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
              <div className="h-40 animate-pulse rounded-lg bg-gray-100" />
            </div>
          )}

          {error && (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          {detalle && resumen && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <ResumenItem
                  label="Remisiones"
                  value={resumen.cantidadRemisiones}
                  hint={resumen.multiplesRemisiones ? 'Varias' : undefined}
                />
                <ResumenItem
                  label="Facturas"
                  value={resumen.cantidadFacturas}
                  hint={resumen.multiplesFacturas ? 'Varias' : undefined}
                />
                <ResumenItem
                  label="FE / FS / FR"
                  value={`${resumen.cantidadFacturasFe} / ${resumen.cantidadFacturasFs} / ${resumen.cantidadFacturasFr}`}
                />
                <ResumenItem
                  label="Notas crédito"
                  value={resumen.cantidadNotasCredito}
                  hint={resumen.multiplesNotasCredito ? 'Varias' : undefined}
                />
              </div>

              {tipos.length > 0 && (
                <div className="flex flex-wrap gap-1.5" aria-label="Tipos de factura">
                  {tipos.map((tipo) => (
                    <OfimaTipoBadge key={tipo} tipo={tipo} />
                  ))}
                </div>
              )}

              {remisiones.length === 0 && (
                <p className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                  Este pedido no tiene remisiones listadas.
                </p>
              )}

              {remisiones.map((remision: OfimaRemisionDetalle, index) => (
                <section key={documentKey(remision, index)} className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <OfimaDocumentoCard documento={remision} />
                  {(remision.facturas ?? []).map((factura, facturaIndex) => (
                    <div key={documentKey(factura, facturaIndex)} className="md:ml-4">
                      <OfimaDocumentoCard documento={factura} nested />
                    </div>
                  ))}
                  {(remision.notasCredito ?? []).map((nota, notaIndex) => (
                    <div key={documentKey(nota, notaIndex)} className="md:ml-4">
                      <OfimaDocumentoCard documento={nota} nested />
                    </div>
                  ))}
                </section>
              ))}

              {adicionales.length > 0 && (
                <section className="space-y-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Documentos adicionales</h4>
                    <p className="mt-1 text-xs text-gray-500">
                      Facturas o notas crédito de este pedido cuya remisión no coincide con las remisiones listadas.
                    </p>
                  </div>
                  {adicionales.map((documento, index) => (
                    <OfimaDocumentoCard key={documentKey(documento, index)} documento={documento} />
                  ))}
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PedidoOfimaDetailModal
